import { useMemo } from 'react';
import {
  ACOUSTIC_CALIBRATION_RADAR_AXES,
  ACOUSTIC_DECISION_RADAR_AXES,
  type AcousticCalibrationSnapshot
} from '../../lib/acoustic/calibrationTypes';
import { extractDecisionRadarValues, extractRadarValues } from '../../lib/acoustic/calibrationSnapshot';
import type { AcousticRoomSignal } from '../../lib/liveLessonTypes';
import CalibrationRadarChart, { averageRadarRecords } from './CalibrationRadarChart';
import CalibrationValueTable from './CalibrationValueTable';

type AcousticCalibrationRadarProps = {
  signal: AcousticRoomSignal | null;
  referenceSnapshot?: AcousticCalibrationSnapshot | null;
  meanTagSnapshots?: AcousticCalibrationSnapshot[] | null;
};

function legacyDecisionRadar(snapshot: AcousticCalibrationSnapshot): Record<string, number> {
  if (snapshot.decisionRadar) return snapshot.decisionRadar;
  return {
    integrated_single_speaker: snapshot.situationScores.single_speaker,
    integrated_conversation: snapshot.situationScores.conversation,
    integrated_babble: snapshot.situationScores.babble,
    integrated_shouting: snapshot.situationScores.shouting
  };
}

export default function AcousticCalibrationRadar({
  signal,
  referenceSnapshot,
  meanTagSnapshots
}: AcousticCalibrationRadarProps) {
  const liveAnalysis = signal ? extractRadarValues(signal) : null;
  const liveDecision = signal ? extractDecisionRadarValues(signal) : null;

  const referenceAnalysis = referenceSnapshot?.radar ?? null;
  const referenceDecision = referenceSnapshot ? legacyDecisionRadar(referenceSnapshot) : null;

  const meanAnalysis = useMemo(() => {
    if (!meanTagSnapshots?.length) return null;
    return averageRadarRecords(meanTagSnapshots, 'radar');
  }, [meanTagSnapshots]);

  const meanDecision = useMemo(() => {
    if (!meanTagSnapshots?.length) return null;
    const keys = ACOUSTIC_DECISION_RADAR_AXES.map((a) => a.key);
    return Object.fromEntries(
      keys.map((key) => [
        key,
        meanTagSnapshots.reduce((sum, snap) => sum + (legacyDecisionRadar(snap)[key] ?? 0), 0) /
          meanTagSnapshots.length
      ])
    );
  }, [meanTagSnapshots]);

  return (
    <div className="space-y-6">
      <CalibrationRadarChart
        title="Analyseparameter (Meyda / Raumdimensionen)"
        axes={ACOUSTIC_CALIBRATION_RADAR_AXES}
        live={liveAnalysis}
        reference={referenceAnalysis}
        meanTag={meanAnalysis}
      />
      <CalibrationRadarChart
        title="Lernrakete-Entscheidung (YAMNet, Fusion, 3s-Mittel)"
        axes={ACOUSTIC_DECISION_RADAR_AXES}
        live={liveDecision}
        reference={referenceDecision}
        meanTag={meanDecision}
        heightClassName="h-[min(400px,52vh)] min-h-[260px]"
      />
      <CalibrationValueTable decisionProfile={signal?.decisionProfile} />
    </div>
  );
}
